import type { LucideIcon } from "lucide-react";
import { pagesSource } from "@/components/layout/palette/pagesSource";
import { organizationsSource } from "@/components/layout/palette/organizationsSource";

/**
 * Command-palette source registry (C-CMS-DS, plans/ps-cms-shell.md T5) — SHARED FILE.
 *
 * Each source is rendered by `CommandPalette` as one group (in array order) when it
 * has at least one matching item; the palette filters every source's items by a
 * case-insensitive substring on `label` (+ `hint`), prefix matches first, at most
 * 8 per group, and only once the query is `minQueryLength` long.
 *
 * `useItems` is a React HOOK, called unconditionally while the palette is mounted;
 * use `open` to defer any fetch until the palette is opened (`enabled: open`).
 *
 * Children add ONLY their own entry (master §2 shared-file rule; keep both on a merge
 * conflict): ps-cms-help appends a "Help articles" source with this same interface.
 */
export interface PaletteItem {
  id: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  href: string;
}

export interface PaletteSource {
  id: string;
  heading: string;
  minQueryLength: number;
  useItems(query: string, open: boolean): { items: PaletteItem[]; isLoading: boolean };
}

export const PALETTE_SOURCES: PaletteSource[] = [pagesSource, organizationsSource];

/** Maximum items shown per palette group. */
export const PALETTE_MAX_ITEMS_PER_GROUP = 8;

/** Case-insensitive substring match on label + hint; label-prefix matches first; capped at `max`. */
export function filterPaletteItems(
  items: PaletteItem[],
  query: string,
  max: number = PALETTE_MAX_ITEMS_PER_GROUP
): PaletteItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items.slice(0, max);
  const prefix: PaletteItem[] = [];
  const rest: PaletteItem[] = [];
  for (const item of items) {
    const label = item.label.toLowerCase();
    if (label.startsWith(q)) prefix.push(item);
    else if (label.includes(q) || (item.hint ?? "").toLowerCase().includes(q)) rest.push(item);
  }
  return [...prefix, ...rest].slice(0, max);
}
